import { type FaceKey, isRequiredFace } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { usePhotoDraftStore } from '@/entities/edit-area';
import { FACE_LABELS } from '../model/face-labels';
import { useFacePhotoUpload } from '../model/use-face-photo-upload';
import { AssetUploadControls } from './asset-upload-controls';

interface FacePhotoCardProps {
  readonly colorOptionId: string;
  readonly colorName: string;
  readonly face: FaceKey;
}

/** 색상·면 하나의 목업 사진 카드. 업로드 조작은 합성 에셋 칸과 같은 조각(AssetUploadControls)이다. */
export const FacePhotoCard = ({
  colorOptionId,
  colorName,
  face,
}: FacePhotoCardProps): ReactElement => {
  const store = usePhotoDraftStore();
  const photo = useStore(
    store,
    (state) =>
      state.draft.colors.find((color) => color.colorOptionId === colorOptionId)?.faces[face],
  );
  const photoUrl = useStore(store, (state) =>
    photo ? state.photoUrls[photo.photoKey] : undefined,
  );
  const { state, upload, retry, cancel } = useFacePhotoUpload(colorOptionId, face);
  const label = `${colorName} ${FACE_LABELS[face]}`;

  return (
    // 저장 전 확인 목록(describe-edit-area-issue.ts)이 이 id 로 이동·포커스한다.
    <article
      id={`edit-area-photo-${colorOptionId}-${face}`}
      tabIndex={-1}
      className="min-w-0 rounded-lg border p-3"
      aria-label={label}
    >
      <h4 className="font-medium">
        {FACE_LABELS[face]}{' '}
        <span className="text-xs text-muted-foreground">
          {isRequiredFace(face) ? '필수' : '선택 면'}
        </span>
      </h4>
      <AssetUploadControls
        label={label}
        subject="사진"
        actionNoun="사진"
        accept="image/png,image/jpeg,image/webp"
        preview={photo ? { url: photoUrl, alt: `${label} 목업`, size: photo.photoSize } : null}
        emptyText="사진 미등록"
        state={state}
        onUpload={(file) => void upload(file)}
        onRetry={() => void retry()}
        onCancel={cancel}
      />
    </article>
  );
};
